import { Box } from "@mantine/core";
import { useMemo } from "react";
import { Layer, Source } from "react-map-gl/maplibre";
import { MapBase } from "shared/components/map/MapBase";
import { MapBoundaryOverlay } from "shared/components/map/MapBoundaryOverlay";
import { MapPolygonDrawOverlay } from "shared/components/map/MapPolygonDrawOverlay";
import { GeoLayer } from "shared/components/organization/geolocations/types";
import { useGeoLocations } from "shared/components/organization/geolocations/useGeoLocations";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { catching } from "shared/utils/fns";
import { maybe } from "shared/utils/maybe";
import { geozod } from "shared/utils/zod";

export const GeoLocationsMap = () => {
    const geo = useGeoLocations();
    const isMobile = useIsMobile();

    return (
        <Box
            style={{
                flex: 1,
                position: "relative",
                padding: isMobile ? 0 : "0.5rem",
                height: "100%",
            }}
        >
            <MapBase id="geolocations-map" style={{ height: "100%" }}>
                <MapBoundaryOverlay />
                <MapLayersOverlay
                    layers={geo.layers}
                    hiddenLayerIds={geo.hiddenLayerIds}
                    hoveredShapeId={geo.hoveredShapeId}
                    excludedShapeId={geo.drawingLayerId ? (geo.editingShape?.id ?? null) : null}
                />
                {geo.drawingLayerId && (
                    <MapPolygonDrawOverlay
                        position="top-left"
                        features={
                            maybe(geo.polygon)?.take((polygon) => [{ type: "Feature" as const, geometry: polygon, properties: {} }]) ?? []
                        }
                        onChange={([feature]) => {
                            if (!feature) return;
                            geo.setPolygon(geozod.polygon().parse(feature.geometry));
                        }}
                    />
                )}
            </MapBase>
        </Box>
    );
};

type MapLayersOverlayProps = {
    layers: GeoLayer[];
    hiddenLayerIds: Record<string, boolean>;
    hoveredShapeId: string | null;
    excludedShapeId: string | null;
};

const MapLayersOverlay = ({ layers, hiddenLayerIds, hoveredShapeId, excludedShapeId }: MapLayersOverlayProps) => {
    // Stable keys for memo
    const layersKey = layers
        .map((layer) => {
            if (!layer.filters) return `${layer.id}:unloaded`;
            return `${layer.id}:${layer.filters.map((filter) => `${filter.id}:${filter.name}:${filter.boundary}`).join("|")}`;
        })
        .join(",");
    const visibilityKey = layers.map((l) => hiddenLayerIds[l.id] === false).join(",");

    const geojson: GeoJSON.FeatureCollection = useMemo(
        () => ({
            type: "FeatureCollection",
            features: layers
                .filter((layer) => hiddenLayerIds[layer.id] === false)
                .flatMap((layer) => layer.filters ?? [])
                .filter((filter) => filter.id !== excludedShapeId)
                .map((filter) => {
                    const geometry = catching(() => geozod.polygon().safeParse(JSON.parse(filter.boundary)).data);
                    return geometry
                        ? ({
                              type: "Feature" as const,
                              geometry,
                              properties: { id: filter.id, name: filter.name },
                          } satisfies GeoJSON.Feature)
                        : null;
                })
                .filter((f): f is NonNullable<typeof f> => f !== null),
        }),
        [layersKey, visibilityKey, excludedShapeId],
    );

    return (
        <Source type="geojson" data={geojson}>
            <Layer
                id="shapes-fill"
                type="fill"
                paint={{
                    "fill-color": ["case", ["==", ["get", "id"], hoveredShapeId ?? ""], "#0088cc", "#0088cc"],
                    "fill-opacity": ["case", ["==", ["get", "id"], hoveredShapeId ?? ""], 0.4, 0.2],
                }}
            />
            <Layer
                id="shapes-line"
                type="line"
                paint={{
                    "line-color": "#0088cc",
                    "line-width": ["case", ["==", ["get", "id"], hoveredShapeId ?? ""], 4, 2],
                }}
            />
        </Source>
    );
};
